import LoadingTask from './LoadingTask';

export default class CompositeLoadingTask extends LoadingTask {
    private _subTasks: LoadingTask[] = [];
    private _waitForTaskAddedCallbacks: { [taskName: string]: ((task: LoadingTask) => void)[] } = {};

    public static get Success(): CompositeLoadingTask {
        return super.Success as CompositeLoadingTask;
    }

    public static get Failed(): CompositeLoadingTask {
        return super.Failed as CompositeLoadingTask;
    }

    public get subTaskCount(): number {
        return this._subTasks.length;
    }

    public findTask(taskName: string) {
        return this._subTasks.find((task) => task.name === taskName);
    }

    public waitForTaskAdded(taskName: string): Promise<LoadingTask> {
        const existTask = this._subTasks.find((task) => taskName === task.name);
        if (existTask) {
            return Promise.resolve(existTask);
        }
        const callbacks = this._waitForTaskAddedCallbacks[taskName] ?? (this._waitForTaskAddedCallbacks[taskName] = []);
        return new Promise<LoadingTask>((resolve) => {
            callbacks.push(resolve);
        });
    }

    public addSimpleTask<T>(task: string, taskPromise: Promise<T>): LoadingTask {
        return this.addTask(new LoadingTask(task, taskPromise));
    }

    public addTask(task: string | LoadingTask): LoadingTask {
        const subTask = task instanceof LoadingTask ? task : new LoadingTask(task);
        if (this._subTasks.find((each) => each.name === subTask.name)) {
            throw new Error(`There is task named ${subTask.name} in the group already`);
        }
        subTask.on(CompositeLoadingTask.EVENT_TYPE.Progress, this._onSubTaskProgress, this);
        subTask.on(CompositeLoadingTask.EVENT_TYPE.Finished, this._onSubTaskFinished, this);
        this._subTasks.push(subTask);
        this._waitForTaskAddedCallbacks[subTask.name]?.forEach((callback) => callback(subTask));
        if (subTask.completeCount) {
            this._onSubTaskProgress();
        }
        if (subTask.finish) {
            this._onSubTaskFinished();
        }
        return subTask;
    }

    public progress(): void {
        throw new Error('TaskGroup shoud not progress by itself');
    }

    public finish(): void {
        throw new Error('TaskGroup shoud not finish by itself');
    }

    private _onSubTaskProgress() {
        super.progress(
            this._subTasks.reduce((pre, cur) => pre + cur.completeCount, 0),
            this._subTasks.reduce((pre, cur) => pre + cur.totalCount, 0),
        );
    }

    private _onSubTaskFinished() {
        if (this._subTasks.every((task) => task.isFinished)) {
            super.finish(
                this._subTasks.every((task) => task.isSuccess)
                    ? null
                    : new Error(`Status: ${this._subTasks.filter((subTask) => !subTask.isSuccess).length}/${this._subTasks.length} Tasks failed`),
            );
        }
    }
}
